/**
 * WHAT: Hooks for managing filter state (useFilters) and simple text search
 *       (useSearchFilter) against arrays of data.
 * WHY:  Nearly every DAMA table (user access reports, campaign lists, application
 *       inventories) needs client-side filtering. These hooks standardize filter
 *       state, active-filter detection, and the apply/clear lifecycle so each
 *       page does not reinvent its own filter useState/handler boilerplate.
 */

import { useState, useMemo, useCallback } from 'react';

/**
 * WHAT: Manages an arbitrary set of named filters (text, dropdown, multi-select)
 *       and applies them to data arrays via a caller-supplied filter function.
 * WHY:  DAMA tables expose multiple filter controls (status, technology,
 *       compliance flags). This hook unifies their state, provides toggle/clear
 *       helpers, and tracks how many filters are active for badge display.
 *
 * useFilters - A hook for managing filter state and applying filters to data.
 * Provides a consistent pattern for search, dropdown filters, and multi-select filters.
 *
 * @param {Object} initialFilters - Initial filter values
 * @param {Object} options - Configuration options
 * @param {Function} options.filterFn - Custom filter function (item, filters) => boolean
 *
 * @returns {Object} Filter state and controls
 *
 * @example
 * const {
 *   filters,
 *   setFilter,
 *   setFilters,
 *   clearFilter,
 *   clearAllFilters,
 *   hasActiveFilters,
 *   activeFilterCount,
 *   applyFilters
 * } = useFilters({
 *   search: '',
 *   status: '',
 *   database: '',
 *   privilegeLevel: []
 * });
 *
 * // Set a single filter
 * setFilter('status', 'ACTIVE');
 *
 * // Set multiple filters at once
 * setFilters({ status: 'ACTIVE', database: 'PROD' });
 *
 * // Apply filters to data
 * const filteredData = applyFilters(allData, (item, filters) => {
 *   if (filters.search && !item.name.includes(filters.search)) return false;
 *   if (filters.status && item.status !== filters.status) return false;
 *   return true;
 * });
 */
export function useFilters(initialFilters = {}, options = {}) {
  const { filterFn: defaultFilterFn } = options;

  const [filters, setFiltersState] = useState(initialFilters);

  // Set a single filter value
  const setFilter = useCallback((key, value) => {
    setFiltersState(prev => ({ ...prev, [key]: value }));
  }, []);

  // Set multiple filter values at once
  const setFilters = useCallback((newFilters) => {
    setFiltersState(prev => ({ ...prev, ...newFilters }));
  }, []);

  // Clear a single filter (reset to initial value)
  const clearFilter = useCallback((key) => {
    setFiltersState(prev => ({
      ...prev,
      [key]: initialFilters[key] !== undefined ? initialFilters[key] : ''
    }));
  }, [initialFilters]);

  // Clear all filters (reset to initial state)
  const clearAllFilters = useCallback(() => {
    setFiltersState(initialFilters);
  }, [initialFilters]);

  // Toggle a value in an array filter (for multi-select)
  const toggleArrayFilter = useCallback((key, value) => {
    setFiltersState(prev => {
      const currentArray = Array.isArray(prev[key]) ? prev[key] : [];
      const newArray = currentArray.includes(value)
        ? currentArray.filter(v => v !== value)
        : [...currentArray, value];
      return { ...prev, [key]: newArray };
    });
  }, []);

  // Check if any filters are active (different from initial values)
  const hasActiveFilters = useMemo(() => {
    return Object.keys(filters).some(key => {
      const currentValue = filters[key];
      const initialValue = initialFilters[key];

      // Handle arrays
      if (Array.isArray(currentValue)) {
        return currentValue.length > 0;
      }

      // Handle strings and other values
      return currentValue !== initialValue && currentValue !== '' && currentValue !== null;
    });
  }, [filters, initialFilters]);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    return Object.keys(filters).reduce((count, key) => {
      const currentValue = filters[key];
      const initialValue = initialFilters[key];

      if (Array.isArray(currentValue)) {
        return count + (currentValue.length > 0 ? 1 : 0);
      }

      if (currentValue !== initialValue && currentValue !== '' && currentValue !== null) {
        return count + 1;
      }

      return count;
    }, 0);
  }, [filters, initialFilters]);

  // Apply filters to an array of items
  const applyFilters = useCallback((items, filterFunction = defaultFilterFn) => {
    if (!filterFunction) {
      console.warn('useFilters: No filter function provided to applyFilters');
      return items;
    }

    return items.filter(item => filterFunction(item, filters));
  }, [filters, defaultFilterFn]);

  // Get filtered items (memoized version for when items don't change often)
  const getFilteredItems = useCallback((items, filterFunction = defaultFilterFn) => {
    return applyFilters(items, filterFunction);
  }, [applyFilters, defaultFilterFn]);

  return {
    // State
    filters,

    // Setters
    setFilter,
    setFilters,
    clearFilter,
    clearAllFilters,
    toggleArrayFilter,

    // Status
    hasActiveFilters,
    activeFilterCount,

    // Apply
    applyFilters,
    getFilteredItems
  };
}

/**
 * WHAT: A lightweight hook that manages a single search term and filters arrays
 *       by matching that term against specified (or all string) fields.
 * WHY:  Many DAMA tables only need a quick-search box without dropdowns or
 *       multi-select. This hook avoids the overhead of useFilters for that
 *       simple case while keeping the same clear/has-search API pattern.
 *
 * useSearchFilter - A simplified hook for search-only filtering.
 * Useful when you only need a text search without other filters.
 *
 * @param {string} initialValue - Initial search value (default: '')
 * @param {Object} options - Configuration options
 * @param {string[]} options.searchFields - Fields to search in items
 * @param {boolean} options.caseSensitive - Case sensitive search (default: false)
 *
 * @returns {Object} Search state and controls
 *
 * @example
 * const { searchTerm, setSearchTerm, clearSearch, filterBySearch } = useSearchFilter('', {
 *   searchFields: ['name', 'email', 'department']
 * });
 *
 * const filteredUsers = filterBySearch(users);
 */
export function useSearchFilter(initialValue = '', options = {}) {
  const {
    searchFields = [],
    caseSensitive = false
  } = options;

  const [searchTerm, setSearchTerm] = useState(initialValue);

  const clearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);

  const filterBySearch = useCallback((items) => {
    if (!searchTerm.trim()) {
      return items;
    }

    const term = caseSensitive ? searchTerm : searchTerm.toLowerCase();

    return items.filter(item => {
      // If no search fields specified, search all string values
      const fieldsToSearch = searchFields.length > 0
        ? searchFields
        : Object.keys(item).filter(key => typeof item[key] === 'string');

      return fieldsToSearch.some(field => {
        const value = item[field];
        if (value == null) return false;

        const stringValue = String(value);
        const compareValue = caseSensitive ? stringValue : stringValue.toLowerCase();

        return compareValue.includes(term);
      });
    });
  }, [searchTerm, searchFields, caseSensitive]);

  const hasSearch = searchTerm.trim().length > 0;

  return {
    searchTerm,
    setSearchTerm,
    clearSearch,
    filterBySearch,
    hasSearch
  };
}

export default useFilters;
